// How a file came to be (Daniil's request, spec docs/specs/web/G1-screens.md decision 30): ⓘ of
// every lightbox (the library's, the Studio's Library tab's and My Generations') shows the
// generation a file came from, from what the job read returns: the prompt with Copy, the type
// and the model, the settings its type takes, its inputs as pictures, who started it, when, how
// long it took and what it was charged (quoted until then), and Clone and the job's own page. A
// field the job has none of is left out, and ids and internals are never shown. The job's details
// read (`GET .../jobs/{job_id}/details`) adds the credits charged, its voices' names, its global
// actors' pictures and the prompt template it came from; a vendor's rewritten prompt is not kept.
import { ActionIcon, Button, CopyButton, Loader, Text, Tooltip } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Check, Copy, ExternalLink, Image as ImageIcon, Music, Package, User } from "lucide-react";
import { ApiError } from "../../api/errors";
import {
  useGetCatalog,
  useGetImportedGeneration,
  useGetJob,
  useGetJobDetails,
} from "../../api/generated/endpoints/generation/generation";
import { useGetProduct, useListMembers } from "../../api/generated/endpoints/teams/teams";
import type {
  ActorSource,
  ExampleRef,
  GenerationOutput,
  GenerationType,
  Job,
  JobGlobalActor,
  JobParams,
} from "../../api/generated/model";
import { creditsText } from "../../ui/credits";
import { formatDateTime } from "../../ui/format";
import { VideoFrame } from "../../ui/VideoFrame";
import { ErrorAlert } from "../ErrorAlert";
import { modelName } from "../jobs/model-name";
import { TYPE_NAMES } from "../jobs/type-names";
import { memberName } from "../members";
import classes from "./GenerationFacts.module.css";
import { DetailList, lengthText, type PreviewDetail } from "./MediaPreview";
import { useRefPicture } from "./ref-picture";

type Words = string | null;

const text = (value: string | number | null | undefined): Words =>
  value === null || value === undefined || value === "" ? null : String(value);
const onOff = (value: boolean | null | undefined): Words => (value === null || value === undefined ? null : value ? "On" : "Off");
const kept = (value: boolean | null | undefined): Words => (value === null || value === undefined ? null : value ? "Kept" : "Removed");
const seconds = (value: number | null | undefined): Words => (value === null || value === undefined ? null : `${value} s`);

/** A language's English name from its code ("es" → "Spanish"), the code where none is known. */
function language(code: string | null | undefined): Words {
  if (code === null || code === undefined || code === "") return null;
  try {
    return new Intl.DisplayNames(["en"], { type: "language" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** Video Upscale's restoration, its sliders that were moved: "Noise 0.2 · Grain 0.1". */
function restoration(params: JobParams): Words {
  const upscale = params.upscale;
  if (upscale === null || upscale === undefined) return null;
  const parts = (
    [
      ["Noise", upscale.noise],
      ["Grain", upscale.grain],
      ["Halo", upscale.halo],
      ["Compression", upscale.compression],
      ["Detail", upscale.recover_detail],
    ] as const
  ).flatMap(([name, value]) => (value === null || value === undefined ? [] : [`${name} ${value}`]));
  return parts.length === 0 ? null : parts.join(" · ");
}

/** A setting ⓘ can show: its label, and its words from the job (null where it has none). */
interface Setting {
  readonly label: string;
  readonly read: (params: JobParams, job: Job) => Words;
}

const SETTINGS = {
  ratio: { label: "Aspect ratio", read: (p) => text(p.aspect_ratio ?? p.talking_head_advanced?.aspect_ratio) },
  resolution: { label: "Resolution", read: (p) => text(p.resolution ?? p.talking_head_advanced?.resolution) },
  duration: { label: "Duration", read: (p) => seconds(p.duration_seconds ?? p.audio?.duration_seconds) },
  outputs: { label: "Outputs", read: (_, job) => (job.outputs.length === 0 ? null : String(job.outputs.length)) },
  sound: { label: "Sound", read: (p) => onOff(p.generate_audio) },
  sourceSound: { label: "Original sound", read: (p) => kept(p.keep_source_audio ?? p.background?.preserve_audio) },
  seed: { label: "Seed", read: (p) => text(p.advanced?.seed ?? p.audio?.seed) },
  enhancer: { label: "Prompt enhancer", read: (p) => onOff(p.advanced?.prompt_enhancer) },
  negative: { label: "Negative prompt", read: (p) => text(p.advanced?.negative_prompt) },
  guidance: { label: "Guidance scale", read: (p) => text(p.advanced?.cfg_scale) },
  fps: {
    label: "Frame rate",
    read: (p) => {
      const fps = p.advanced?.fps ?? p.upscale?.target_fps;
      return fps === null || fps === undefined ? null : `${fps} fps`;
    },
  },
  bitrate: { label: "Bitrate", read: (p) => text(p.advanced?.bitrate) },
  upscale: {
    label: "Upscale",
    read: (p) => (p.upscale?.upscale_factor === null || p.upscale?.upscale_factor === undefined ? null : `${p.upscale.upscale_factor}×`),
  },
  restoration: { label: "Restoration", read: restoration },
  background: { label: "Background", read: (p) => text(p.background?.background_color) },
  subject: {
    label: "Subject",
    read: (p) => {
      const person = p.background?.subject_is_person;
      return person === null || person === undefined ? null : person ? "A person" : "An object";
    },
  },
  from: { label: "From", read: (p) => (p.dubbing === null || p.dubbing === undefined ? null : (language(p.dubbing.source_lang) ?? "Detected")) },
  to: { label: "To", read: (p) => language(p.dubbing?.target_lang) },
  speakers: { label: "Speakers", read: (p) => text(p.dubbing?.num_speakers) },
  backgroundSound: {
    label: "Background sound",
    read: (p) => {
      const drop = p.dubbing?.drop_background_audio;
      return drop === null || drop === undefined ? null : drop ? "Removed" : "Kept";
    },
  },
  syncMode: { label: "Sync mode", read: (p) => text(p.lipsync?.sync_mode) },
  findFace: { label: "Find the face", read: (p) => onOff(p.lipsync?.auto_detect) },
  instrumental: { label: "Instrumental", read: (p) => onOff(p.audio?.force_instrumental) },
  language: { label: "Language", read: (p) => language(p.audio?.language_code) },
  loop: { label: "Loop", read: (p) => onOff(p.audio?.loop) },
  influence: { label: "Prompt influence", read: (p) => text(p.audio?.prompt_influence) },
  stability: { label: "Stability", read: (p) => text(p.audio?.stability) },
  gender: {
    label: "Gender",
    read: (p) => (p.actor === null || p.actor === undefined ? null : `${p.actor.gender.charAt(0).toUpperCase()}${p.actor.gender.slice(1)}`),
  },
  vibe: { label: "Vibe", read: (p) => text(p.talking_head_advanced?.vibe) },
} as const satisfies Record<string, Setting>;

type SettingKey = keyof typeof SETTINGS;

const IMAGE: readonly SettingKey[] = ["ratio", "resolution", "outputs", "seed", "enhancer", "negative", "guidance"];
const VIDEO: readonly SettingKey[] = [
  "ratio",
  "resolution",
  "duration",
  "outputs",
  "sound",
  "seed",
  "enhancer",
  "negative",
  "guidance",
  "fps",
  "bitrate",
];
const SOUND: readonly SettingKey[] = ["duration", "outputs", "instrumental", "language", "loop", "influence", "stability", "seed"];
const NO_SETTINGS: readonly SettingKey[] = [];

/**
 * The settings ⓘ shows for each type, in its order: those its form takes. One per type, so a new
 * type fails tsc until it says what its panel shows. A text job leaves no file, so shows none.
 */
const SHOWN: { readonly [T in GenerationType]: readonly SettingKey[] } = {
  "audio.extract": NO_SETTINGS,
  "audio.music": SOUND,
  "audio.sfx": SOUND,
  "audio.tts": SOUND,
  "audio.voiceover": SOUND,
  "caption.transcribe": NO_SETTINGS,
  "extract.brand": NO_SETTINGS,
  "extract.product": NO_SETTINGS,
  "extract.typography": NO_SETTINGS,
  "extract.colors": NO_SETTINGS,
  "image.actor": ["gender", "outputs"],
  "image.advanced": IMAGE,
  "image.analysis": NO_SETTINGS,
  // Its prompt and its layer's picture; its ratio is the one nearest the layer's (G13b).
  "image.layer_edit": ["ratio"],
  // Its prompt and its source picture only; its outputs are the layers FAL may answer.
  "image.layerize": NO_SETTINGS,
  "image.simple": IMAGE,
  // A template run: its tile's facts come with My Generations (G14 PR 4).
  "image.template": NO_SETTINGS,
  "image.tweak": IMAGE,
  "text.actor_description": NO_SETTINGS,
  "text.ad_copy": NO_SETTINGS,
  "text.enhance": NO_SETTINGS,
  "text.generate": NO_SETTINGS,
  "text.script": NO_SETTINGS,
  "text.shot_list": NO_SETTINGS,
  "video.audio": NO_SETTINGS,
  "video.background": ["background", "subject", "sourceSound"],
  "video.caption": NO_SETTINGS,
  "video.dub": ["from", "to", "speakers", "backgroundSound"],
  "video.edit": [...VIDEO, "sourceSound"],
  "video.extend": VIDEO,
  "video.frames": NO_SETTINGS,
  "video.image": VIDEO,
  "video.lipsync": ["syncMode", "findFace"],
  "video.merge": NO_SETTINGS,
  "video.motion": VIDEO,
  "video.overlay": NO_SETTINGS,
  "video.reference": VIDEO,
  "video.reframe": VIDEO,
  "video.shots": NO_SETTINGS,
  "video.splice": NO_SETTINGS,
  "video.pixel_snap": NO_SETTINGS,
  "video.talking_head": NO_SETTINGS,
  "video.talking_head_advanced": ["vibe", "ratio", "resolution"],
  // A template run, as a picture template's.
  "video.template": NO_SETTINGS,
  "video.text": VIDEO,
  "video.upscale": ["upscale", "fps", "restoration"],
  "video.voice_swap": NO_SETTINGS,
  "voice.clone": NO_SETTINGS,
  "voice.design": NO_SETTINGS,
  "voice.profile": NO_SETTINGS,
};

/** What the prompt is to a type whose prompt is not one: the words a voice reads. */
const PROMPT_LABEL: Partial<Record<GenerationType, string>> = { "audio.tts": "Text", "audio.voiceover": "Text" };

/** One input of the job, by its part in it: a file, an output, an actor, or a product. */
type Input = { label: string; ref: ActorSource } | { label: string; productId: string };

/** The job's inputs, in the order its form takes them. */
export function inputsOf(job: Job): Input[] {
  const p = job.params;
  if (p === null) return [];
  const inputs: Input[] = [];
  // A card's example input (G14) is the catalog's file, not the team's: only a template run's
  // steps name one, and the run's own facts stand for them, so it is not listed.
  const one = (label: string, ref: ActorSource | ExampleRef | null | undefined) => {
    if (ref !== null && ref !== undefined && ref.kind !== "example") inputs.push({ label, ref });
  };
  const many = (label: string, refs: readonly (ActorSource | ExampleRef)[] | null | undefined) => {
    const all = (refs ?? []).filter((ref): ref is ActorSource => ref.kind !== "example");
    all.forEach((ref, at) => inputs.push({ label: all.length === 1 ? label : `${label} ${at + 1}`, ref }));
  };
  // A tweak's first reference is the picture it tweaks (G6), a layerize's the one it splits (G13).
  if (job.type === "image.tweak" || job.type === "image.layerize") {
    one("Source image", p.references?.[0]);
    many("Reference", p.references?.slice(1));
  } else {
    many("Reference", p.references);
  }
  one("Start frame", p.start_frame);
  one("End frame", p.end_frame);
  many("Reference image", p.reference_images);
  many("Reference video", p.reference_videos);
  many("Reference audio", p.reference_audios);
  one("Source video", p.source_video);
  one("Source audio", p.source_audio);
  (p.elements ?? []).forEach((element, at) => {
    one(`Element ${at + 1}`, element.frontal);
    many(`Element ${at + 1} image`, element.images);
    one(`Element ${at + 1} video`, element.video);
  });
  one("AI Actor", p.talking_head?.actor ?? p.talking_head_advanced?.actor);
  one("Audio", p.talking_head?.source_audio);
  one("Inspiration", p.ad_copy?.reference);
  const product = p.ad_copy?.product_id ?? p.script?.product_id ?? p.talking_head_advanced?.product_id;
  if (product !== null && product !== undefined) inputs.push({ label: "Product", productId: product });
  return inputs;
}

/** How long a job took: "45 s", "1 min 12 s", "1 h 3 min". */
export function tookText(ms: number): string {
  const total = Math.max(1, Math.round(ms / 1000));
  if (total < 60) return `${total} s`;
  const minutes = Math.floor(total / 60);
  if (minutes < 60) return total % 60 === 0 ? `${minutes} min` : `${minutes} min ${total % 60} s`;
  return minutes % 60 === 0 ? `${Math.floor(minutes / 60)} h` : `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

/** Words as written, with Copy. */
function Copyable(props: { value: string; what: string }) {
  return (
    <>
      <p className={classes.text}>{props.value}</p>
      <CopyButton value={props.value}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? "Copied" : `Copy ${props.what}`}>
            <ActionIcon
              className={classes.copy}
              variant="subtle"
              color="gray"
              c="var(--cai-color-viewer-text)"
              size="sm"
              aria-label={copied ? `${props.what.charAt(0).toUpperCase()}${props.what.slice(1)} copied` : `Copy ${props.what}`}
              onClick={copy}
            >
              {copied ? <Check size={14} aria-hidden /> : <Copy size={14} aria-hidden />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </>
  );
}

function Glyph({ kind }: { kind: "image" | "video" | "audio" | "actor" | "product" | null }) {
  if (kind === "audio") return <Music size={20} aria-hidden />;
  if (kind === "actor") return <User size={20} aria-hidden />;
  if (kind === "product") return <Package size={20} aria-hidden />;
  return <ImageIcon size={20} aria-hidden />;
}

/** A file, an output or an actor the job took, its picture read as the composer reads one. */
function RefInput(props: { teamId: string; brandId: string; label: string; refInput: ActorSource; globalActors: readonly JobGlobalActor[] }) {
  const { teamId, brandId, label, refInput } = props;
  const read = useRefPicture(teamId, brandId, refInput);
  // A global actor's portrait comes with the job's details (a team's own is read by its id).
  const global = refInput.kind === "global_actor" ? props.globalActors.find((each) => each.id === refInput.actor_id) : undefined;
  const picture = global === undefined ? read : { ...read, still: global.image_url, kind: "image" as const, name: global.name };
  const actor = refInput.kind === "actor" || refInput.kind === "global_actor";
  const name = picture.name ?? label;
  return (
    <li className={classes.input} title={picture.name ?? undefined}>
      <span className={classes.thumb}>
        {picture.still !== null ? (
          <img src={picture.still} alt={name} />
        ) : picture.video !== null ? (
          <VideoFrame src={picture.video} label={name} still fallback={<Glyph kind="video" />} />
        ) : (
          <Glyph kind={actor ? "actor" : picture.kind} />
        )}
      </span>
      <span className={classes.caption}>{label}</span>
    </li>
  );
}

/** A product the job was made for: its main picture and its name. */
function ProductInput({ teamId, brandId, productId }: { teamId: string; brandId: string; productId: string }) {
  const product = useGetProduct(teamId, brandId, productId).data;
  const image = product?.images.find((each) => each.is_main === true) ?? product?.images[0];
  const still = image?.url ?? null;
  return (
    <li className={classes.input} title={product?.name}>
      <span className={classes.thumb}>
        {still === null ? <Glyph kind="product" /> : <img src={still} alt={product?.name ?? "Product"} />}
      </span>
      <span className={classes.caption}>{product?.name ?? "Product"}</span>
    </li>
  );
}

export interface GenerationFactsProps {
  teamId: string;
  /** The brand on show: the job's own is read first, for its products. */
  brandId: string;
  job: Job;
  /** The output on show, for its size and length (My Generations); a library file has its own. */
  output?: GenerationOutput | undefined;
  /** Offers the job's own page: in the library, where the generation is not on show. */
  openable?: boolean | undefined;
  /** A link was followed: the preview closes behind it. */
  onLeave?: (() => void) | undefined;
}

/** ⓘ's account of a generation, from its job. */
export function GenerationFacts(props: GenerationFactsProps) {
  const { teamId, job, output } = props;
  const brandId = job.brand_id ?? props.brandId;
  const members = useListMembers(teamId).data ?? [];
  const catalog = useGetCatalog({ query: { enabled: job.model !== null } }).data;
  const details = useGetJobDetails(teamId, job.id).data;
  const params = job.params;
  const rows: PreviewDetail[] = [];
  const prompt = params?.prompt?.trim() ?? "";
  if (prompt !== "") {
    const label = PROMPT_LABEL[job.type] ?? "Prompt";
    rows.push({ label, value: <Copyable value={prompt} what={label.toLowerCase()} /> });
  } else if (output?.mime?.startsWith("image/") === true && (params?.shots ?? []).length === 0) {
    // Live's words for a picture with no prompt.
    rows.push({ label: "Prompt", value: "This image doesn't have a prompt or structure yet." });
  }
  const shots = params?.shots ?? [];
  if (shots.length > 0) {
    rows.push({
      label: "Shots",
      value: (
        <ol className={classes.shots}>
          {shots.map((shot, at) => (
            <li key={at}>
              <span className={classes.shotLength}>{shot.duration_seconds} s</span>
              {shot.prompt === undefined || shot.prompt === "" ? null : <p className={classes.text}>{shot.prompt}</p>}
            </li>
          ))}
        </ol>
      ),
    });
  }
  const script = params?.talking_head_advanced?.script.trim() ?? "";
  if (script !== "") rows.push({ label: "Script", value: <Copyable value={script} what="script" /> });
  const style = params?.style?.trim() ?? "";
  if (style !== "") rows.push({ label: "Style", value: <Copyable value={style} what="style" /> });
  const template = details?.prompt_template;
  if (template !== null && template !== undefined) rows.push({ label: "Prompt template", value: template.name });
  rows.push({ label: "Type", value: TYPE_NAMES[job.type].name });
  if (job.model !== null) rows.push({ label: "Model", value: modelName(catalog, job.model) });
  if (params !== null) {
    for (const key of SHOWN[job.type]) {
      const setting: Setting = SETTINGS[key];
      const value = setting.read(params, job);
      if (value !== null) rows.push({ label: setting.label, value });
    }
  }
  const voices = details?.voices ?? [];
  if (voices.length > 0) {
    rows.push({ label: voices.length === 1 ? "Voice" : "Voices", value: voices.map((voice) => voice.name).join(", ") });
  }
  const inputs = inputsOf(job);
  if (inputs.length > 0) {
    rows.push({
      label: "Inputs",
      value: (
        <ul className={classes.inputs}>
          {inputs.map((input, at) =>
            "ref" in input ? (
              <RefInput
                key={at}
                teamId={teamId}
                brandId={brandId}
                label={input.label}
                refInput={input.ref}
                globalActors={details?.global_actors ?? []}
              />
            ) : (
              <ProductInput key={at} teamId={teamId} brandId={brandId} productId={input.productId} />
            ),
          )}
        </ul>
      ),
    });
  }
  if (output !== undefined) {
    if (output.width !== null && output.height !== null) rows.push({ label: "Size", value: `${output.width} × ${output.height}` });
    if (output.duration_ms !== null) rows.push({ label: "Length", value: lengthText(output.duration_ms) });
  }
  rows.push(
    {
      label: "Started by",
      value: job.created_by === null ? "Unknown" : memberName(members.find((member) => member.user_id === job.created_by)),
    },
    { label: "Started", value: formatDateTime(job.created_at) },
  );
  if (job.finished_at !== null) {
    const took = Date.parse(job.finished_at) - Date.parse(job.started_at ?? job.created_at);
    if (Number.isFinite(took) && took >= 0) rows.push({ label: "Took", value: tookText(took) });
  }
  // What it was charged in the end, with a run's steps; quoted until it is charged.
  const charged = details?.charged_credits;
  rows.push(
    charged === null || charged === undefined
      ? { label: "Quoted", value: creditsText(job.quote) }
      : { label: "Charged", value: `${charged} ${charged === 1 ? "credit" : "credits"}` },
  );
  const clone = TYPE_NAMES[job.type].clone && job.brand_id !== null;
  return (
    <>
      <DetailList details={rows} />
      {clone || props.openable === true ? (
        <div className={classes.links}>
          {clone ? (
            <Button
              size="xs"
              variant="white"
              color="dark"
              leftSection={<Copy size={14} aria-hidden />}
              onClick={props.onLeave}
              renderRoot={(root) => (
                <Link
                  {...root}
                  to="/teams/$teamId/brands/$brandId/generate"
                  params={{ teamId, brandId }}
                  search={{ ...(job.type.startsWith("video.") ? { medium: "video" as const } : {}), clone: job.id }}
                />
              )}
            >
              Clone
            </Button>
          ) : null}
          {props.openable === true ? (
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              c="var(--cai-color-viewer-text)"
              leftSection={<ExternalLink size={14} aria-hidden />}
              onClick={props.onLeave}
              renderRoot={(root) => <Link {...root} to="/teams/$teamId/jobs/$jobId" params={{ teamId, jobId: job.id }} />}
            >
              Open generation
            </Button>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

/** What ⓘ says of a saved copy whose generation was deleted (its maker's "Delete generation"). */
export function GenerationGone() {
  return <Text size="sm">Its generation is no longer kept.</Text>;
}

/**
 * A library file's generation: its job, read here, so only once ⓘ opens, and cached by its id
 * as the card menu's read is. A job deleted since says so; a failed read says what failed.
 */
export function JobFacts(props: { teamId: string; brandId: string; jobId: string; onLeave?: (() => void) | undefined }) {
  const read = useGetJob(props.teamId, props.jobId);
  if (read.isPending) return <Loader size="xs" color="var(--cai-color-viewer-muted)" aria-label="Reading how it was made" />;
  if (read.isError) {
    return read.error instanceof ApiError && read.error.status === 404 ? (
      <GenerationGone />
    ) : (
      <ErrorAlert error={read.error} />
    );
  }
  return <GenerationFacts teamId={props.teamId} brandId={props.brandId} job={read.data} openable onLeave={props.onLeave} />;
}

/**
 * A file a brand import brought in from live as a generation (#828 bug 24): ⓘ's Info as live's
 * Library shows it (`AssetModalInfo`): its prompt as written, or live's line for none, read only
 * once ⓘ opens. An Advanced creative's structure is its Clone's, never ⓘ's, as on live.
 */
export function ImportedFacts(props: { teamId: string; brandId: string; assetId: string }) {
  const read = useGetImportedGeneration(props.teamId, props.brandId, props.assetId);
  if (read.isPending) return <Loader size="xs" color="var(--cai-color-viewer-muted)" aria-label="Reading how it was made" />;
  const none = read.isError && read.error instanceof ApiError && read.error.status === 404;
  if (read.isError && !none) return <ErrorAlert error={read.error} />;
  const prompt = read.data?.prompt ?? null;
  if (prompt === null) {
    return (
      <Text size="sm" c="dimmed">
        No additional information available
      </Text>
    );
  }
  return <DetailList details={[{ label: "Prompt", value: <p className={classes.text}>{prompt}</p> }]} />;
}
